import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { Cita, Documento, EntradaHistorial, MotivoCancelacion, Pase, VistaPreviaCancelacion } from '../../api/ops/citas';
import type { FichaPago } from '../../api/ops/pagos';
import { Ayuda } from '../../componentes/Ayuda';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoNoContratado } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { navegador } from '../../logica/navegador';
import { Avatar } from '../../marco/piezas';
import type { ContextoAgenda } from './contexto';
import { VentanaNoVino } from './NoVino';
import {
    activa,
    canal,
    cargoNoShow,
    diaCorto,
    duracion,
    enCurso,
    estadoVisible,
    franjaDe,
    hm,
    horaEn,
    iniciales,
    lineaPase,
    mayuscula,
    momento,
    primerNombre,
    puedeLlegada,
    puedeNoVino,
    telefonoLegible,
    telefonoOculto,
    terminada,
    textoDeError,
    textoCobroFallido,
    textoServicios,
    textoTrasNoVino,
} from './logica';
import { AYUDA, CANCELADA_POR, COBRO, FIRMA, MOTIVOS, MOTIVOS_NEGOCIO, MOTIVOS_PROFESIONAL, PESTANAS, type PestanaCita } from './textos';
import { BotonAg } from './Vistas';

/**
 * Ficha de la cita (9.3) en RV Panel con su botonera Cita · Cobro · Firma · Historial (`?cita=&tab=`, G779). Todo lo que
 * cambia la cita lo decide la API (`actions`); el panel solo esconde lo que va a fallar. Tras deshacer un no-show se
 * pinta el estado que devuelve la API.
 */
type Ventanas = 'noVino' | 'deshacer' | 'cancelar' | 'enlacePago' | 'reintentar' | 'enlaceFirma' | 'tablet' | 'bloqueado' | null;

interface Nota {
    texto: string;
    icono: NombreIcono;
    enlace?: string;
}

/** Valor de la cancelación por el profesional en la API (no es un rol: es quién cancela). */
const POR_EL_PROFESIONAL = `professional` as const;

export function FichaCita({ ctx, id, pestana, onPestana, onCerrar, onCambiar, notaInicial }: { ctx: ContextoAgenda; id: string; pestana: PestanaCita; onPestana: (p: PestanaCita) => void; onCerrar: () => void; onCambiar: (c: ConVersion<Cita>) => void; notaInicial?: Nota | null }) {
    const [leida, setLeida] = useState<ConVersion<Cita> | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [pase, setPase] = useState<Pase | null>(null);
    const [ventana, setVentana] = useState<Ventanas>(null);
    const [nota, setNota] = useState<Nota | null>(notaInicial ?? null);

    useEffect(() => {
        let vivo = true;
        ctx.ops
            .getAppointment(ctx.negocio, id)
            .then((c) => {
                if (vivo) {
                    setLeida(c);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        ctx.ops
            .listAppointmentPasses(ctx.negocio, id)
            .then((l) => {
                if (vivo) {
                    setPase(l.data.find((p) => p.status !== 'voided') ?? l.data[0] ?? null);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ctx.ops, ctx.negocio, id, intento]);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    /** La API devolvió la cita cambiada: se pinta tal cual y la agenda vuelve a leer. */
    const ponerCita = useCallback(
        (c: Cita, n: Nota | null) => {
            setLeida((l) => ({ datos: c, version: l?.version ?? null }));
            setVentana(null);
            setNota(n);
            ctx.actualizada(c);
            setIntento((i) => i + 1);
        },
        [ctx],
    );

    const c = leida?.datos ?? null;
    const nombre = c === null ? '' : ctx.nombreDe(c);
    const pro = c === null ? undefined : ctx.profesional(c.segments[0]?.professional_id);
    const proNombre = pro?.display_name ?? c?.segments[0]?.professional_display_name ?? '';
    const conFirma = c !== null && c.consent_status !== 'not_required';
    const conCobro = c !== null && (c.payment_status !== 'not_required' || (c.payments ?? []).length > 0 || c.deposit != null || c.payment_waived === true);
    const pestanas = PESTANAS.filter(([p]) => (p !== 'firma' || conFirma) && (p !== 'cobro' || conCobro));
    const actual: PestanaCita = pestanas.some(([p]) => p === pestana) ? pestana : 'cita';
    const ro = ctx.soloLectura;

    // Botón de la cabecera según el estado (pAccion del diseño).
    let accion: { texto: string; icono: NombreIcono; fn: () => void } | null = null;
    if (c !== null) {
        if (!ro && c.status === 'pending_completion' && ctx.puede.completar) {
            accion = { texto: 'Vino', icono: 'check', fn: () => { ctx.marcarVino(c); } };
        } else if (ctx.puede.llegada && puedeLlegada(c, ctx.ahora.fecha)) {
            accion = { texto: 'Registrar la llegada', icono: 'log-in', fn: () => { ctx.registrarLlegada(c); } };
        } else if (!ro && c.status === 'pending_payment' && ctx.puede.gestionar) {
            accion = { texto: 'Reenviar el enlace', icono: 'send', fn: () => { setVentana('enlacePago'); } };
        }
    }

    const cuerpo = (() => {
        if (c === null) {
            return error !== null ? <EstadoError seccion="la cita" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={ctx.movil} /> : <EstadoCargando seccion="la cita" />;
        }
        switch (actual) {
            case 'cobro':
                return <Cobro ctx={ctx} c={c} onReintentar={() => { setVentana('reintentar'); }} />;
            case 'firma':
                return <Firma ctx={ctx} c={c} nombre={nombre} onTablet={() => { setVentana('tablet'); }} onReenviar={() => { setVentana('enlaceFirma'); }} onBloqueado={() => { setVentana('bloqueado'); }} onCambiar={() => { if (leida !== null) { onCambiar(leida); } }} />;
            case 'historial':
                return <Historial ctx={ctx} c={c} />;
            default:
                return (
                    <Detalle
                        ctx={ctx}
                        c={c}
                        nombre={nombre}
                        proNombre={proNombre}
                        pase={pase}
                        onVentana={setVentana}
                        onCambiar={() => {
                            if (leida !== null) {
                                onCambiar(leida);
                            }
                        }}
                    />
                );
        }
    })();

    const [estadoTxt, estadoChip] = c === null ? ['', 'neutral' as EstadoChip] : estadoVisible(c, ctx.ahora);
    const fr = c === null ? null : franjaDe(c);

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={ctx.movil}
                tamano="ficha"
                eyebrow={fr === null ? 'Cita' : `Cita · ${hm(fr.inicio)}`}
                titulo={nombre}
                subtitulo={c === null || fr === null ? undefined : `${mayuscula(diaCorto(fr.fecha))} · ${hm(fr.inicio)} – ${hm(fr.fin)}${proNombre === '' ? '' : ` · con ${primerNombre(proNombre)}`}`}
                chip={c === null ? undefined : estadoTxt}
                chipEstado={estadoChip}
                accion={accion?.texto ?? ''}
                accionIcono={accion?.icono}
                onAccion={accion?.fn}
            >
                <div className="rv-ag-ficha">
                    <div className="rv-ag-botonera" role="tablist" aria-label="Apartados de la cita">
                        {pestanas.map(([clave, texto, icono]) => (
                            <button
                                key={clave}
                                type="button"
                                role="tab"
                                aria-selected={actual === clave}
                                className="rv-ag-botonera__b"
                                onClick={() => {
                                    setNota(null);
                                    onPestana(clave);
                                }}
                            >
                                <Icono nombre={icono} />
                                <span>{texto}</span>
                            </button>
                        ))}
                    </div>
                    <div className="rv-ag-ficha__contenido">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {c !== null && ctx.movil && <Avatar texto={iniciales(nombre)} tamano={40} fondo="var(--rv-ink)" tinta="#fff" />}
                            <h3 className="rv-ag-ceja">{PESTANAS.find(([p]) => p === actual)?.[1] ?? ''}</h3>
                            <span style={{ flex: 1 }} />
                            <Ayuda tarjetas={AYUDA[actual]} />
                        </div>
                        {nota !== null && (
                            <div className="rv-ag-caja" role="status" style={{ background: 'var(--rv-bone)' }}>
                                <div className="rv-ag-caja__titulo">
                                    <Icono nombre={nota.icono} />
                                    <span style={{ fontWeight: 600 }}>{nota.texto}</span>
                                </div>
                                {nota.enlace !== undefined && (
                                    <div className="rv-ag-acciones">
                                        <BotonAg
                                            texto="Copiar el enlace"
                                            icono="copy"
                                            onClick={() => {
                                                void navigator.clipboard.writeText(nota.enlace ?? '').catch(() => undefined);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {cuerpo}
                    </div>
                </div>
            </PanelLateral>
            {c !== null && (
                <>
                    <VentanaNoVino
                        ctx={ctx}
                        cita={ventana === 'noVino' ? c : null}
                        onCerrar={() => {
                            setVentana(null);
                        }}
                        onHecho={(x) => {
                            ponerCita(x, { texto: textoTrasNoVino(x, nombre), icono: 'user-x' });
                        }}
                    />
                    <VentanaDeshacer ctx={ctx} c={c} abierta={ventana === 'deshacer'} onCerrar={() => { setVentana(null); }} onHecho={(x) => { ponerCita(x, { texto: 'No presentado deshecho. Si se cobró el cargo, se le devuelve.', icono: 'undo-2' }); }} />
                    <VentanaCancelar ctx={ctx} c={c} nombre={nombre} abierta={ventana === 'cancelar'} onCerrar={() => { setVentana(null); }} onHecho={ponerCita} />
                    <VentanaSimple
                        abierta={ventana === 'enlacePago'}
                        movil={ctx.movil}
                        titulo="Reenviar el enlace de pago"
                        texto={`Se lo enviamos otra vez a ${nombre} por WhatsApp (${telefonoOculto(ctx.telefonoDe(c))}), con el email de respaldo. No alarga el plazo: caduca a las ${horaEn(c.payment_expires_at, ctx.zona)}.`}
                        accion="Reenviar"
                        icono="send"
                        ocupadoTexto="Enviando…"
                        onCerrar={() => { setVentana(null); }}
                        hacer={() =>
                            ctx.ops.sendAppointmentPaymentLink(ctx.negocio, c.id).then((p) => {
                                setVentana(null);
                                setNota({ texto: `Enlace reenviado a ${primerNombre(nombre)} por WhatsApp. Sigue caducando a las ${horaEn(p.expires_at, ctx.zona)}.`, icono: 'send' });
                            })
                        }
                    />
                    <VentanaReintentar ctx={ctx} c={c} nombre={nombre} abierta={ventana === 'reintentar'} onCerrar={() => { setVentana(null); }} onHecho={(texto) => { setVentana(null); setNota({ texto, icono: 'circle-check' }); recargar(); }} />
                    <VentanaSimple
                        abierta={ventana === 'enlaceFirma'}
                        movil={ctx.movil}
                        titulo="Reenviar el enlace de firma"
                        texto="Se lo enviamos a su WhatsApp, con el email de respaldo. Como mucho 3 reenvíos al día."
                        accion="Reenviar"
                        icono="send"
                        ocupadoTexto="Enviando…"
                        onCerrar={() => { setVentana(null); }}
                        hacer={() =>
                            ctx.ops.resendSignatureLink(ctx.negocio, c.document_id ?? '').then(() => {
                                setVentana(null);
                                setNota({ texto: `Enlace de firma reenviado a ${primerNombre(nombre)}.`, icono: 'send' });
                            })
                        }
                    />
                    <VentanaSimple
                        abierta={ventana === 'tablet'}
                        movil={ctx.movil}
                        titulo="Firmar en la tablet"
                        texto={`Abrimos la firma del consentimiento en la tablet del centro, en «Firmas pendientes». ${primerNombre(nombre)} lee el documento y firma allí, con la verificación del proveedor.`}
                        accion="Abrir en la tablet"
                        icono="tablet"
                        ocupadoTexto="Abriendo…"
                        onCerrar={() => { setVentana(null); }}
                        hacer={() => {
                            // 9.13 en esta pantalla, con «‹ Panel» de vuelta a esta cita (la tablet la abre con la sesión con PIN).
                            ctx.navegar(`/tablet/firmas?cita=${encodeURIComponent(c.id)}&origen=panel`);
                            return Promise.resolve();
                        }}
                    />
                    <VentanaBloqueado ctx={ctx} c={c} abierta={ventana === 'bloqueado'} onCerrar={() => { setVentana(null); }} />
                </>
            )}
        </>
    );
}

// ---- Apartado «Cita» ----

function Caja({ fondo, tinta, icono, titulo, texto, children }: { fondo: string; tinta: string; icono: NombreIcono; titulo: string; texto: string; children?: ReactNode }) {
    return (
        <div className="rv-ag-caja" style={{ background: fondo, color: tinta }}>
            <div className="rv-ag-caja__titulo">
                <Icono nombre={icono} />
                <span>{titulo}</span>
            </div>
            {texto !== '' && <p>{texto}</p>}
            {children !== undefined && <div className="rv-ag-acciones">{children}</div>}
        </div>
    );
}

function Linea({ k, v, sub }: { k: string; v: string; sub?: string }) {
    return (
        <div className="rv-ag-lineas__l">
            <span>{k}</span>
            <span className="rv-ag-lineas__v">
                <strong>{v}</strong>
                {sub !== undefined && sub !== '' && <small>{sub}</small>}
            </span>
        </div>
    );
}

function Detalle({ ctx, c, nombre, proNombre, pase, onVentana, onCambiar }: { ctx: ContextoAgenda; c: Cita; nombre: string; proNombre: string; pase: Pase | null; onVentana: (v: Ventanas) => void; onCambiar: () => void }) {
    const fr = franjaDe(c);
    const ro = ctx.soloLectura;
    const z = ctx.zona;
    const hoy = ctx.ahora.fecha;
    let caja: ReactNode = null;
    if (c.status === 'pending_payment') {
        caja = (
            <Caja fondo="var(--rv-warning-soft)" tinta="var(--rv-warning-ink)" icono="hourglass" titulo={`Esperando el pago de la señal (${dinero(c.deposit)})`} texto={`Le enviamos el enlace a las ${horaEn(c.created_at, z)}. Caduca a las ${horaEn(c.payment_expires_at, z)}: si no paga, la cita caduca y el hueco se libera.`}>
                {!ro && ctx.puede.gestionar ? <BotonAg texto="Reenviar el enlace de pago" icono="send" onClick={() => { onVentana('enlacePago'); }} /> : undefined}
            </Caja>
        );
    } else if (c.status === 'pending_completion') {
        caja = (
            <Caja fondo="var(--rv-warning-soft)" tinta="var(--rv-warning-ink)" icono="circle-alert" titulo="Terminó sin que nadie marcara la llegada" texto="Indica si vino. Mientras tanto queda por completar y el equipo lo ve en la campana.">
                {!ro && ctx.puede.noShow ? <BotonAg texto="No vino" icono="user-x" tipo="rojo" onClick={() => { onVentana('noVino'); }} /> : undefined}
            </Caja>
        );
    } else if (c.status === 'no_show') {
        caja = (
            <Caja fondo="var(--rv-red-soft)" tinta="var(--rv-ink)" icono="user-x" titulo="No se presentó" texto={`Lo marcó el equipo (${momento(c.no_show_at, z, hoy)}).${c.payment_status === 'charge_failed' ? ' El cobro por no presentarse ha fallado: míralo en «Cobro».' : ''}`}>
                {!ro && ctx.puede.deshacerNoShow ? <BotonAg texto="Deshacer el no presentado" icono="undo-2" onClick={() => { onVentana('deshacer'); }} /> : undefined}
            </Caja>
        );
    } else if (c.status === 'cancelled') {
        const x = c.cancellation;
        caja = (
            <Caja
                fondo="var(--rv-bone)"
                tinta="var(--rv-ink)"
                icono="ban"
                titulo={`Cancelada ${x == null ? '' : (CANCELADA_POR[x.cancelled_by] ?? '')}`.trim()}
                texto={x == null ? '' : `${MOTIVOS[x.reason_code] ?? 'Otro motivo'} · ${momento(x.cancelled_at, z, hoy)}.${x.refund != null && x.refund.amount > 0 ? ` Se le devuelven ${dinero(x.refund)}.` : ''}${x.note != null && x.note !== '' ? ` Nota: «${x.note}».` : ''}`}
            />
        );
    } else if (c.status === 'arrived') {
        caja = <Caja fondo="var(--rv-ink)" tinta="#fff" icono="log-in" titulo={`Llegó a las ${horaEn(c.arrived_at, z)}`} texto="La cita se completa sola al terminar su franja, porque la llegada está registrada." />;
    } else if (c.status === 'completed') {
        caja = <Caja fondo="var(--rv-bone)" tinta="var(--rv-ink)" icono="circle-check" titulo="Completada" texto={c.arrived_at != null ? `Llegó a las ${horaEn(c.arrived_at, z)}.` : 'El equipo indicó que vino.'} />;
    } else if (enCurso(c, ctx.ahora)) {
        caja = (
            <Caja
                fondo="var(--rv-bone)"
                tinta="var(--rv-ink)"
                icono="clock"
                titulo="Debería estar aquí ya"
                texto={`Empezó a las ${hm(fr.inicio)}. Registra la llegada al recibirla.${c.actions.can_mark_no_show_from != null ? ` Si no viene, desde las ${horaEn(c.actions.can_mark_no_show_from, z)} puedes marcar que no vino.` : ''}`}
            />
        );
    }

    const lineaPaseCita = lineaPase(c, pase, proNombre);
    const acciones: ReactNode[] = [];
    if (!ro && ctx.puede.noShow && c.status === 'confirmed' && puedeNoVino(c)) {
        acciones.push(<BotonAg key="nv" texto="No vino" icono="user-x" tipo="rojo" onClick={() => { onVentana('noVino'); }} />);
    }
    if (!ro && c.actions.can_modify) {
        acciones.push(<BotonAg key="cambiar" texto="Cambiar" icono="pencil" onClick={onCambiar} />);
    }
    if (ctx.puede.verClientes && c.customer_id !== null) {
        const ruta = ctx.rutaDe('cliente', c.customer_id) ?? `/clientes?cliente=${encodeURIComponent(c.customer_id)}`;
        acciones.push(<BotonAg key="ficha" texto="Ver la ficha" icono="user" onClick={() => { ctx.navegar(ruta); }} />);
    }
    if (!ro && c.actions.can_cancel) {
        acciones.push(<BotonAg key="cancelar" texto="Cancelar la cita" icono="ban" tipo="rojo" onClick={() => { onVentana('cancelar'); }} />);
    }
    let notaAcciones = '';
    const aj = ctx.ajustes;
    if (ro) {
        notaAcciones = 'El negocio está en solo lectura: puedes consultar la cita y registrar la llegada, pero no cambiarla.';
    } else if (ctx.esProfesional && aj !== null && (c.status === 'confirmed' || c.status === 'pending_payment') && (!aj.professional_can_move_own || !aj.professional_can_cancel_own)) {
        notaAcciones = !aj.professional_can_move_own && !aj.professional_can_cancel_own
            ? 'Tu negocio no deja a los profesionales cambiar ni cancelar sus citas. Pídeselo a recepción.'
            : !aj.professional_can_move_own
              ? 'Tu negocio no deja a los profesionales cambiar sus citas. Pídeselo a recepción.'
              : 'Tu negocio no deja a los profesionales cancelar sus citas. Pídeselo a recepción.';
    }
    const varios = c.segments.length > 1;

    return (
        <>
            {caja}
            <div className="rv-ag-lineas">
                <Linea k="Cuándo" v={`${mayuscula(diaCorto(fr.fecha))} · ${hm(fr.inicio)} – ${hm(fr.fin)}`} sub={duracion(c.total_duration_minutes)} />
                <Linea k="Profesional" v={proNombre} sub={c.professional_choice === 'any' ? 'La clienta dijo «cualquiera»' : 'Elegido al pedirla'} />
                {c.customer_id === null ? (
                    <Linea k="Cliente" v="Cliente suprimido" sub="Pidió borrar sus datos: la cita se queda sin nombre" />
                ) : (
                    <Linea k="Cliente" v={nombre} sub={telefonoLegible(ctx.telefonoDe(c))} />
                )}
                <Linea k="Pedida por" v={canal(c)} sub={momento(c.created_at, ctx.zona, ctx.ahora.fecha)} />
                <Linea k="Código" v={c.code} sub="Para recepción y la clienta" />
                <Linea k="Pase" v={lineaPaseCita.valor} sub={lineaPaseCita.sub} />
            </div>
            <h4 className="rv-ag-subtitulo">Servicios</h4>
            <div>
                {c.segments.map((s) => {
                    const ini = horaEn(s.starts_at, c.timezone);
                    const fin = horaEn(s.ends_at, c.timezone);
                    const respuestas = s.answers.map((a) => `${a.question_label}: ${(a.option_label ?? (a.number_value != null ? String(a.number_value) : (a.text_value ?? ''))).toLocaleLowerCase('es')}${a.minutes_applied > 0 ? ` (+${String(a.minutes_applied)} min)` : ''}`);
                    return (
                        <div key={s.seq} className="rv-ag-servicio">
                            <span>
                                <strong>{s.service_name ?? ''}</strong>
                                <small>{[`${ini} – ${fin}`, duracion(s.duration_minutes), ...respuestas].join(' · ')}</small>
                            </span>
                            <strong>{dinero(s.price)}</strong>
                        </div>
                    );
                })}
                <div className="rv-ag-total">
                    <span>{varios ? `Total · ${duracion(c.total_duration_minutes)}` : 'Total'}</span>
                    <strong>{dinero(c.total_price)}</strong>
                </div>
            </div>
            {acciones.length > 0 && <div className="rv-ag-acciones">{acciones}</div>}
            {notaAcciones !== '' && <p className="rv-ag-pie">{notaAcciones}</p>}
        </>
    );
}

// ---- Apartado «Cobro» ----

const CONCEPTO: Readonly<Record<string, readonly [string, NombreIcono]>> = {
    deposit: ['Señal', 'credit-card'],
    prepayment: ['Pago por adelantado', 'credit-card'],
    guarantee: ['Garantía', 'credit-card'],
    difference: ['Diferencia', 'credit-card'],
    no_show_fee: ['Cargo por no presentarse', 'banknote'],
    late_cancellation_fee: ['Cargo por cancelar tarde', 'banknote'],
    refund: ['Devolución de la señal', 'rotate-ccw'],
};

const ESTADO_MOVIMIENTO: Readonly<Record<string, readonly [string, EstadoChip]>> = {
    pending: ['Pendiente', 'pending'],
    completed: ['Cobrado', 'confirmed'],
    failed: ['Fallido', 'problem'],
    refunded: ['Reembolsado', 'neutral'],
    expired: ['Caducado', 'neutral'],
};

function Cobro({ ctx, c, onReintentar }: { ctx: ContextoAgenda; c: Cita; onReintentar: () => void }) {
    const fallido = c.payment_status === 'charge_failed' ? cargoNoShow(c) : null;
    const [pago, setPago] = useState<FichaPago | null>(null);
    useEffect(() => {
        if (fallido === null) {
            return;
        }
        let vivo = true;
        ctx.ops
            .getPayment(ctx.negocio, fallido.payment_id)
            .then((p) => {
                if (vivo) {
                    setPago(p);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ctx.ops, ctx.negocio, fallido?.payment_id, fallido]);

    const [estado, chip] = c.payment_waived === true ? (['Sin señal', 'neutral'] as const) : COBRO[c.payment_status];
    const pagos = c.payments ?? [];
    const intentos = pago?.attempts?.length ?? 0;
    const ultimo = pago?.attempts?.[pago.attempts.length - 1];
    const hasta = c.free_cancellation_until;
    const z = ctx.zona;
    const rutaPago = fallido === null ? null : (ctx.rutaDe('pago', fallido.payment_id) ?? `/pagos?apartado=pagos&pago=${encodeURIComponent(fallido.payment_id)}`);

    return (
        <>
            {fallido !== null && (
                <Caja
                    fondo="var(--rv-red-soft)"
                    tinta="var(--rv-ink)"
                    icono="triangle-alert"
                    titulo={`El cobro de ${dinero(fallido.amount)} por no presentarse ha fallado`}
                    texto={textoCobroFallido(intentos, ultimo?.decline_label)}
                >
                    {ctx.puede.reintentarCobro && !ctx.soloLectura && intentos < 3 ? <BotonAg texto="Reintentar el cobro" icono="refresh-cw" tipo="pri" onClick={onReintentar} /> : null}
                    {ctx.puede.verPagos && rutaPago !== null ? <BotonAg texto="Ver en Pagos" icono="credit-card" onClick={() => { ctx.navegar(rutaPago); }} /> : null}
                </Caja>
            )}
            <div className="rv-ag-lineas">
                <div className="rv-ag-lineas__l">
                    <span>Estado del cobro</span>
                    <Chip estado={chip}>{estado}</Chip>
                </div>
                <Linea k="Señal" v={c.payment_waived === true ? 'No se cobra' : c.deposit != null && c.deposit.amount > 0 ? `${dinero(c.deposit)}${c.segments.length > 1 ? ' (suma de los servicios)' : ''}` : 'No pide señal'} />
                {hasta != null && <Linea k="Cancelación gratis" v={`Hasta ${mayuscula(diaCorto(enFecha(hasta, z)))}, ${horaEn(hasta, z)}`} />}
            </div>
            <h4 className="rv-ag-subtitulo">Movimientos</h4>
            {pagos.length === 0 ? (
                <p className="rv-ag-pie">Todavía no hay movimientos.</p>
            ) : (
                <div>
                    {pagos.map((p) => {
                        const [t, icono] = CONCEPTO[p.concept] ?? [p.concept, 'credit-card'];
                        const [et, est] = ESTADO_MOVIMIENTO[p.status] ?? [p.status, 'neutral'];
                        return (
                            <div key={p.payment_id} className="rv-ag-mov">
                                <Icono nombre={icono} />
                                <div>
                                    <strong>{t}</strong>
                                    <small>{momento(p.created_at, z, ctx.ahora.fecha)}</small>
                                </div>
                                <Chip estado={est}>{et}</Chip>
                                <strong>{p.concept === 'refund' ? `−${dinero(p.amount)}` : dinero(p.amount)}</strong>
                            </div>
                        );
                    })}
                </div>
            )}
            <p className="rv-ag-pie">
                {fallido !== null && !ctx.puede.reintentarCobro
                    ? 'Reintentar el cobro a mano lo hace el propietario o un administrador.'
                    : 'La política de cobro queda fijada al pedir la cita: si después cambias la política, esta cita no cambia.'}
            </p>
        </>
    );
}

function enFecha(instante: string, zona: string): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(instante));
}

// ---- Apartado «Firma» ----

function Firma({ ctx, c, nombre, onTablet, onReenviar, onBloqueado, onCambiar }: { ctx: ContextoAgenda; c: Cita; nombre: string; onTablet: () => void; onReenviar: () => void; onBloqueado: () => void; onCambiar: () => void }) {
    const [doc, setDoc] = useState<Documento | null>(null);
    const [error, setError] = useState<unknown>(null);
    const avisar = useAvisoBreve();
    useEffect(() => {
        if (c.document_id == null) {
            return;
        }
        let vivo = true;
        ctx.ops
            .getDocument(ctx.negocio, c.document_id)
            .then((d) => {
                if (vivo) {
                    setDoc(d);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ctx.ops, ctx.negocio, c.document_id]);

    if (error !== null && estadoDeError(error).tipo === 'no-contratado') {
        return (
            <EstadoNoContratado
                bloque="Firma eIDAS"
                incluye={['Consentimiento por servicio, con su plantilla', 'Firma en el móvil de la clienta o en la tablet', '25 firmas al mes incluidas']}
                propietario={false}
                onAnadir={null}
                onInicio={null}
                movil={ctx.movil}
            />
        );
    }
    const cs = c.status === 'cancelled' ? 'voided' : c.consent_status;
    const [chipTxt, chipEst] = FIRMA[cs] ?? ['', 'neutral'];
    const bloqueado = cs === 'signed' && (doc?.blocked_at != null || c.customer_id === null);
    const firmadoEl = doc?.signed_at != null ? `Firmado el ${momento(doc.signed_at, ctx.zona, '')}${doc.reused_from_document_id != null ? ' · reutilizado: sigue vigente' : ''}` : '';
    const primero = primerNombre(nombre);
    const textos: Readonly<Record<string, string>> = {
        signed: doc?.reused_from_document_id != null ? `${primero} ya lo había firmado y sigue vigente, así que no se le ha pedido otra vez.` : `${primero} lo firmó con su móvil y verificó su identidad con el proveedor de firma.`,
        requested: 'Le enviamos el enlace al confirmar la cita. Vence 2 horas antes de empezar. Si no le llega, puede firmar en la tablet del centro.',
        pending: 'No firmó a tiempo. La cita no se cancela: puede firmar en la tablet del centro cuando venga, o cambias la cita.',
        voided: 'Se anuló al cancelar la cita.',
    };
    const abrir = (p: Promise<{ url: string }>) => {
        p.then((f) => {
            navegador.abrir(f.url);
        }).catch((e: unknown) => {
            avisar({ tipo: 'error', texto: textoDeError(e) });
        });
    };
    const botones: ReactNode[] = [];
    if (cs === 'signed' && ctx.puede.verDocumento && !bloqueado && c.document_id != null) {
        const docId = c.document_id;
        botones.push(<BotonAg key="ver" texto="Ver el documento firmado" icono="download" onClick={() => { abrir(ctx.ops.getSignedDocumentFile(ctx.negocio, docId)); }} />);
    }
    if (bloqueado && ctx.puede.bloqueados) {
        botones.push(<BotonAg key="bloq" texto="Abrir con motivo legal" icono="lock-open" onClick={onBloqueado} />);
    }
    if ((cs === 'pending' || cs === 'requested') && ctx.puede.recogerFirma && activa(c) && !ctx.soloLectura && c.document_id != null) {
        // Firmar en la tablet (9.13): en cualquier momento mientras esté pedido o pendiente (G478).
        botones.push(<BotonAg key="tablet" texto="Firmar en la tablet" icono="tablet" tipo={cs === 'pending' ? 'pri' : 'sec'} onClick={onTablet} />);
        botones.push(<BotonAg key="reenviar" texto="Reenviar el enlace de firma" icono="send" onClick={onReenviar} />);
    }
    if (cs === 'pending' && c.actions.can_modify && !ctx.soloLectura) {
        botones.push(<BotonAg key="cambiar" texto="Cambiar la cita" icono="pencil" onClick={onCambiar} />);
    }
    const pie = bloqueado
        ? 'Documento bloqueado (cliente suprimido o negocio cerrado): se conserva el plazo legal y solo se abre con un motivo legal, que queda en Actividad.'
        : cs === 'signed' && !ctx.puede.verDocumento
          ? 'El documento firmado solo lo ven el propietario, los administradores y el profesional de la cita.'
          : '';
    return (
        <>
            <div className="rv-ag-caja" style={{ background: 'var(--rv-bone)' }}>
                <div className="rv-ag-caja__titulo">
                    <Icono nombre={cs === 'signed' ? 'file-text' : 'signature'} />
                    <span style={{ flex: 1 }}>
                        {doc?.title ?? 'Consentimiento'}
                        <small style={{ display: 'block', fontWeight: 500, fontSize: 13, color: 'var(--rv-fg-2)' }}>{cs === 'signed' && firmadoEl !== '' ? firmadoEl : 'Firma eIDAS avanzada'}</small>
                    </span>
                    {chipTxt !== '' && <Chip estado={chipEst}>{chipTxt}</Chip>}
                </div>
                <p>{textos[cs] ?? ''}</p>
                {botones.length > 0 && <div className="rv-ag-acciones">{botones}</div>}
            </div>
            {pie !== '' && <p className="rv-ag-pie">{pie}</p>}
        </>
    );
}

// ---- Apartado «Historial» ----

const HISTORIAL: Readonly<Record<EntradaHistorial['kind'], readonly [NombreIcono, string]>> = {
    created: ['calendar-plus', 'Cita pedida'],
    confirmed: ['circle-check', 'Confirmada'],
    modified: ['pencil', 'Cambiada'],
    cancelled: ['ban', 'Cancelada'],
    expired: ['hourglass', 'Caducada sin pagar'],
    arrived: ['log-in', 'Llegó'],
    arrival_undone: ['undo-2', 'Llegada deshecha'],
    pending_completion: ['circle-alert', 'Por completar'],
    completed: ['circle-check', 'Completada'],
    no_show: ['user-x', 'No presentado'],
    no_show_undone: ['undo-2', 'No presentado deshecho'],
    payment: ['credit-card', 'Pago'],
    consent: ['signature', 'Consentimiento firmado'],
    relocated: ['calendar-clock', 'Recolocada'],
};

const ORIGEN: Readonly<Record<string, string>> = { panel: 'Panel', tablet: 'Tablet', web: 'Web', widget: 'Widget', assistant: 'Asistente', api: 'Integración', system: 'Reservaria' };

function Historial({ ctx, c }: { ctx: ContextoAgenda; c: Cita }) {
    const [lista, setLista] = useState<EntradaHistorial[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    useEffect(() => {
        let vivo = true;
        ctx.ops
            .listAppointmentHistory(ctx.negocio, c.id, { limit: 100 })
            .then((l) => {
                if (vivo) {
                    setLista(l.data);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ctx.ops, ctx.negocio, c.id, intento]);
    if (lista === null) {
        return error !== null ? <EstadoError seccion="el historial" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={ctx.movil} /> : <EstadoCargando seccion="el historial" />;
    }
    const ordenada = [...lista].sort((a, b) => b.at.localeCompare(a.at));
    return (
        <>
            {ordenada.length === 0 ? (
                <p className="rv-ag-pie">Todavía no hay movimientos.</p>
            ) : (
                <div className="rv-ag-historial">
                    {ordenada.map((e) => {
                        const [icono, titulo] = HISTORIAL[e.kind];
                        const t = e.kind === 'created' ? `Cita pedida por ${canal(c)}` : e.kind === 'arrived' ? `Llegó a las ${horaEn(e.at, ctx.zona)}` : titulo;
                        const sub = [momento(e.at, ctx.zona, ctx.ahora.fecha), ORIGEN[e.channel] ?? e.channel, e.reason != null && e.reason !== '' ? `«${e.reason}»` : null].filter((x) => x !== null && x !== '').join(' · ');
                        return (
                            <div key={`${e.at}${e.kind}`} className="rv-ag-historial__e">
                                <span>
                                    <Icono nombre={icono} tamano={16} />
                                </span>
                                <div>
                                    <strong>{t}</strong>
                                    <small>{sub}</small>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <p className="rv-ag-pie">Nada se borra: una cita cancelada queda aquí con su historial.</p>
        </>
    );
}

// ---- Ventanas ----

function VentanaSimple({ abierta, movil, titulo, texto, accion, icono, ocupadoTexto, onCerrar, hacer }: { abierta: boolean; movil: boolean; titulo: string; texto: string; accion: string; icono: NombreIcono; ocupadoTexto: string; onCerrar: () => void; hacer: () => Promise<void> }) {
    const [ocupado, setOcupado] = useState(false);
    const avisar = useAvisoBreve();
    return (
        <Ventana
            abierta={abierta}
            titulo={titulo}
            texto={texto}
            cancelar="Volver"
            accion={accion}
            accionIcono={icono}
            ocupado={ocupado}
            ocupadoTexto={ocupadoTexto}
            movil={movil}
            onCerrar={() => {
                if (!ocupado) {
                    onCerrar();
                }
            }}
            onAccion={() => {
                setOcupado(true);
                hacer()
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e) });
                    })
                    .finally(() => {
                        setOcupado(false);
                    });
            }}
        />
    );
}

function VentanaDeshacer({ ctx, c, abierta, onCerrar, onHecho }: { ctx: ContextoAgenda; c: Cita; abierta: boolean; onCerrar: () => void; onHecho: (c: Cita) => void }) {
    const [motivo, setMotivo] = useState('');
    const [error, setError] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const avisar = useAvisoBreve();
    const vuelve = terminada(c, ctx.ahora) ? '«Por completar»' : '«Confirmada»';
    return (
        <Ventana
            abierta={abierta}
            titulo="Deshacer el no presentado"
            texto={`La cita vuelve a ${vuelve}.${c.payment_status === 'charge_failed' ? ' El cargo pendiente se anula.' : ' Si ya se le cobró el cargo, se le devuelve.'}`}
            cancelar="Volver"
            accion="Deshacer"
            accionIcono="undo-2"
            ocupado={ocupado}
            ocupadoTexto="Deshaciendo…"
            movil={ctx.movil}
            onCerrar={() => {
                if (!ocupado) {
                    onCerrar();
                }
            }}
            onAccion={() => {
                if (motivo.trim() === '') {
                    setError('Escribe el motivo.');
                    return;
                }
                setOcupado(true);
                ctx.ops
                    .undoAppointmentNoShow(ctx.negocio, c.id, motivo.trim())
                    .then((x) => {
                        setMotivo('');
                        onHecho(x);
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e) });
                    })
                    .finally(() => {
                        setOcupado(false);
                    });
            }}
        >
            <Campo etiqueta="Motivo" valor={motivo} onChange={(v) => { setMotivo(v); setError(''); }} error={error} ayuda="Queda en Auditoría a tu nombre." max={200} mayusculas="sentences" />
        </Ventana>
    );
}

function VentanaCancelar({ ctx, c, nombre, abierta, onCerrar, onHecho }: { ctx: ContextoAgenda; c: Cita; nombre: string; abierta: boolean; onCerrar: () => void; onHecho: (c: Cita, n: Nota) => void }) {
    const quien = ctx.esProfesional ? POR_EL_PROFESIONAL : 'business';
    const motivos = ctx.esProfesional ? MOTIVOS_PROFESIONAL : MOTIVOS_NEGOCIO;
    const [motivo, setMotivo] = useState<MotivoCancelacion>(motivos[0] ?? 'other');
    const [nota, setNota] = useState('');
    const [vista, setVista] = useState<VistaPreviaCancelacion | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const avisar = useAvisoBreve();
    useEffect(() => {
        if (!abierta) {
            return;
        }
        let vivo = true;
        ctx.ops
            .getAppointmentCancellationPreview(ctx.negocio, c.id, quien)
            .then((v) => {
                if (vivo) {
                    setVista(v);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [abierta, ctx.ops, ctx.negocio, c.id, quien]);
    const fr = franjaDe(c);
    const devuelve = vista !== null && vista.refund.amount > 0;
    const texto = `${mayuscula(diaCorto(fr.fecha))}, ${hm(fr.inicio)} · ${textoServicios(c)}. ${devuelve ? `Como cancelas tú, se le devuelven los ${dinero(vista.refund)} de la señal y no hay cargo.` : 'No ha pagado nada, así que no hay devolución.'} Le avisamos por WhatsApp.`;
    return (
        <Ventana
            abierta={abierta}
            titulo={`Cancelar la cita de ${primerNombre(nombre)}`}
            texto={vista === null ? '' : texto}
            cancelar="Volver"
            accion="Cancelar la cita"
            accionIcono="ban"
            ocupado={ocupado}
            ocupadoTexto="Cancelando…"
            inactiva={vista === null}
            movil={ctx.movil}
            onCerrar={() => {
                if (!ocupado) {
                    onCerrar();
                }
            }}
            onAccion={() => {
                setOcupado(true);
                ctx.ops
                    .cancelAppointment(ctx.negocio, c.id, { cancelled_by: quien, reason_code: motivo, notify_customer: true, ...(nota.trim() === '' ? {} : { note: nota.trim() }) })
                    .then((x) => {
                        const refund = x.cancellation?.refund ?? vista?.refund;
                        onHecho(x, { texto: `Cita cancelada. Avisamos a ${primerNombre(nombre)}${refund != null && refund.amount > 0 ? ` y le devolvemos ${dinero(refund)}` : ''}; su pase ya no vale.`, icono: 'ban' });
                        setNota('');
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e) });
                    })
                    .finally(() => {
                        setOcupado(false);
                    });
            }}
        >
            <Desplegable etiqueta="Motivo" valor={motivo} opciones={motivos.map((m) => ({ valor: m, texto: MOTIVOS[m] ?? m }))} onChange={(v) => { setMotivo(v as MotivoCancelacion); }} />
            <Campo etiqueta="Nota para la clienta" valor={nota} onChange={setNota} ayuda="Opcional. Va en el aviso que le enviamos." max={500} mayusculas="sentences" />
        </Ventana>
    );
}

function VentanaReintentar({ ctx, c, nombre, abierta, onCerrar, onHecho }: { ctx: ContextoAgenda; c: Cita; nombre: string; abierta: boolean; onCerrar: () => void; onHecho: (texto: string) => void }) {
    const cargo = cargoNoShow(c);
    const [intentos, setIntentos] = useState(0);
    const [ocupado, setOcupado] = useState(false);
    const avisar = useAvisoBreve();
    useEffect(() => {
        if (!abierta || cargo === null) {
            return;
        }
        let vivo = true;
        ctx.ops
            .getPayment(ctx.negocio, cargo.payment_id)
            .then((p) => {
                if (vivo) {
                    setIntentos(p.attempts?.length ?? 0);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [abierta, ctx.ops, ctx.negocio, cargo]);
    return (
        <Ventana
            abierta={abierta && cargo !== null}
            titulo="Reintentar el cobro"
            texto={`Intentamos otra vez el cargo sobre la tarjeta que guardó ${primerNombre(nombre)} (intento ${String(Math.max(1, intentos) + 1)} de 3). Solo cuenta como cobrado si el banco lo confirma.`}
            cancelar="Volver"
            accion={cargo === null ? 'Cobrar' : `Cobrar ${dinero(cargo.amount)}`}
            accionIcono="refresh-cw"
            ocupado={ocupado}
            ocupadoTexto="Cobrando…"
            movil={ctx.movil}
            onCerrar={() => {
                if (!ocupado) {
                    onCerrar();
                }
            }}
            onAccion={() => {
                if (cargo === null) {
                    return;
                }
                setOcupado(true);
                ctx.ops
                    .retryOffSessionCharge(ctx.negocio, cargo.payment_id)
                    .then((r) => {
                        // La API responde con el intento (PaymentAttempt): solo `succeeded` cuenta como cobrado (D-079).
                        const intento = r as unknown as { result?: string; decline_label?: string | null };
                        if (intento.result === 'succeeded') {
                            onHecho('Cobro hecho. Lo verás también en Pagos.');
                        } else {
                            avisar({ tipo: 'error', texto: intento.decline_label ?? 'El banco no ha confirmado el cobro.' });
                            onCerrar();
                        }
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e) });
                    })
                    .finally(() => {
                        setOcupado(false);
                    });
            }}
        />
    );
}

function VentanaBloqueado({ ctx, c, abierta, onCerrar }: { ctx: ContextoAgenda; c: Cita; abierta: boolean; onCerrar: () => void }) {
    const [motivo, setMotivo] = useState('');
    const [error, setError] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const avisar = useAvisoBreve();
    return (
        <Ventana
            abierta={abierta}
            titulo="Abrir con motivo legal"
            texto="Documento bloqueado (cliente suprimido o negocio cerrado): se conserva el plazo legal y solo se abre con un motivo legal, que queda en Actividad."
            cancelar="Volver"
            accion="Abrir"
            accionIcono="lock-open"
            ocupado={ocupado}
            ocupadoTexto="Abriendo…"
            movil={ctx.movil}
            onCerrar={() => {
                if (!ocupado) {
                    onCerrar();
                }
            }}
            onAccion={() => {
                if (motivo.trim() === '' || c.document_id == null) {
                    setError('Escribe el motivo.');
                    return;
                }
                setOcupado(true);
                ctx.ops
                    .getBlockedDocumentFile(ctx.negocio, c.document_id, motivo.trim())
                    .then((f) => {
                        navegador.abrir(f.url);
                        setMotivo('');
                        onCerrar();
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e) });
                    })
                    .finally(() => {
                        setOcupado(false);
                    });
            }}
        >
            <Campo etiqueta="Motivo" valor={motivo} onChange={(v) => { setMotivo(v); setError(''); }} error={error} max={200} mayusculas="sentences" />
        </Ventana>
    );
}
